import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import {
  Modal,
  Table,
  Tag,
  Space,
  Tooltip,
  Progress,
  Alert,
  Segmented,
  Descriptions,
  Spin,
} from "antd";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  MdCloudUpload,
  MdRefresh,
  MdErrorOutline,
  MdDownload,
  MdHotel,
} from "react-icons/md";
import * as XLSX from "xlsx";
import { alojamientoProveedorApi, TIPO_OPERACION } from "../../../services/api/alojamientoProveedorApi";
import "react-toastify/dist/ReactToastify.css";

const FILTRO_ESTADO = {
  TODOS: "todos",
  PENDIENTES: "pendientes",
  EN_COLA: "en_cola",
  ENVIADOS: "enviados",
  ERRORES: "errores",
  SIN_HOMOLOGAR: "sin_homologar",
  BLOQUEADOS: "bloqueados",
};

const FILTRO_VIGENCIA = {
  ACTIVOS: "activos",
  INACTIVOS: "inactivos",
  TODOS: "todos",
};

const usaFiltroVigencia = (tipo) => tipo === TIPO_OPERACION.CHECKIN;

const tieneFechaOperacion = (valor) => {
  if (valor == null) return false;
  const texto = String(valor).trim();
  return texto !== "" && texto !== "—";
};

const timestampFechaListado = (valor) => {
  if (!tieneFechaOperacion(valor)) return 0;
  const texto = String(valor).trim();
  const match = texto.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (match) {
    return Date.UTC(
      Number(match[3]),
      Number(match[2]) - 1,
      Number(match[1]),
      Number(match[4] || 0),
      Number(match[5] || 0),
      Number(match[6] || 0)
    );
  }
  const parsed = Date.parse(texto);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const compararFechaListado = (a, b) => timestampFechaListado(a) - timestampFechaListado(b);

const cumpleVigencia = (record, tipo, vigencia) => {
  if (!usaFiltroVigencia(tipo) || vigencia === FILTRO_VIGENCIA.TODOS) return true;
  const esActivo = !tieneFechaOperacion(record.fecha_check_out);
  return vigencia === FILTRO_VIGENCIA.ACTIVOS ? esActivo : !esActivo;
};

const ETIQUETAS_TIPO = {
  [TIPO_OPERACION.CHECKIN]: "Check-in",
  [TIPO_OPERACION.CHECKOUT]: "Check-out",
};

const SUBTITULO_TIPO = {
  [TIPO_OPERACION.CHECKIN]:
    "Por defecto solo check-in activos (sin check-out). Se envían a Logiflex sin exigir reserva previa ni fechas de reserva.",
  [TIPO_OPERACION.CHECKOUT]:
    "Finalizadas con check-out en SGC, las más recientes primero (hasta 10.000). Si falta el check-in Logiflex de la misma asignación, se sincroniza solo antes del check-out.",
};

const LIMITE_LISTADO = 10000;

const RESUMEN_INICIAL = {
  total: 0,
  listadas: 0,
  limite: LIMITE_LISTADO,
  truncado: false,
  enviados_proveedor: 0,
  pendientes: 0,
  en_cola: 0,
  procesando: 0,
  errores: 0,
  sin_homologar: 0,
  bloqueados: 0,
};

const extraerMensajeError = (error, fallback) => {
  const msg =
    error.response?.data?.detail ||
    error.response?.data?.message ||
    fallback;
  return typeof msg === "string" ? msg : JSON.stringify(msg);
};

const normalizarEnviadoProveedor = (valor) =>
  valor === true || valor === 1 || valor === "1";

const esEnvioExitoso = (record) =>
  normalizarEnviadoProveedor(record.enviado_proveedor) ||
  record.estado_envio === "exitoso";

const tieneCadenaAuto = (reserva) =>
  Array.isArray(reserva.cadena_auto) && reserva.cadena_auto.length > 0;

const clasificarEstado = (reserva, tipoOperacion) => {
  if (esEnvioExitoso(reserva)) return FILTRO_ESTADO.ENVIADOS;

  if (!reserva.cama_homologada) {
    return FILTRO_ESTADO.SIN_HOMOLOGAR;
  }

  if (reserva.estado_envio === "error") return FILTRO_ESTADO.ERRORES;
  if (
    reserva.estado_envio === "en_cola" ||
    reserva.estado_envio === "procesando" ||
    reserva.estado_envio === "reintento"
  ) {
    return FILTRO_ESTADO.EN_COLA;
  }
  // Bloqueados: no se puede sincronizar y no hay cadena automática que lo resuelva
  if (reserva.puede_sincronizar === false && !tieneCadenaAuto(reserva)) {
    return FILTRO_ESTADO.BLOQUEADOS;
  }
  return FILTRO_ESTADO.PENDIENTES;
};

const contarPorEstado = (lista, tipoOperacion) => {
  const contadores = {
    [FILTRO_ESTADO.TODOS]: lista.length,
    [FILTRO_ESTADO.PENDIENTES]: 0,
    [FILTRO_ESTADO.EN_COLA]: 0,
    [FILTRO_ESTADO.ENVIADOS]: 0,
    [FILTRO_ESTADO.ERRORES]: 0,
    [FILTRO_ESTADO.SIN_HOMOLOGAR]: 0,
    [FILTRO_ESTADO.BLOQUEADOS]: 0,
  };
  lista.forEach((r) => {
    const estado = clasificarEstado(r, tipoOperacion);
    contadores[estado] = (contadores[estado] || 0) + 1;
  });
  return contadores;
};

const hayTrabajoEnCola = (enColaVisible, cola) => {
  const redisPendiente =
    (cola?.pendientes || 0) + (cola?.reintentos_programados || 0);
  return redisPendiente > 0 || (enColaVisible || 0) > 0;
};

const colaRedisVaciaPeroBdPendiente = (enColaVisible, cola) => {
  const redisPendiente =
    (cola?.pendientes || 0) + (cola?.reintentos_programados || 0);
  return (enColaVisible || 0) > 0 && redisPendiente === 0;
};

const truncarTexto = (texto, max = 120) => {
  if (!texto) return "—";
  const s = String(texto);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const puedeVerDetalleError = (record) =>
  record.estado_envio === "error" || record.estado_envio === "reintento";

const estaEnCola = (record) =>
  record.estado_envio === "en_cola" || record.estado_envio === "procesando";

const SincronizacionReservasLogiflex = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");

  const [loading, setLoading] = useState(false);
  const [enviandoId, setEnviandoId] = useState(null);
  const [enviandoTodos, setEnviandoTodos] = useState(false);
  const [progresoEnvio, setProgresoEnvio] = useState(null);
  const [reservas, setReservas] = useState([]);
  const [resumen, setResumen] = useState(RESUMEN_INICIAL);
  const [monitoreandoCola, setMonitoreandoCola] = useState(false);
  const [colaHuerfanaDetectada, setColaHuerfanaDetectada] = useState(false);
  const pollsColaHuerfanaRef = useRef(0);
  const [tipoOperacion, setTipoOperacion] = useState(TIPO_OPERACION.CHECKIN);
  const [filtroEstado, setFiltroEstado] = useState(FILTRO_ESTADO.TODOS);
  const [modalErrorOpen, setModalErrorOpen] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [loadingErrorDetalle, setLoadingErrorDetalle] = useState(false);
  const [exportandoErrores, setExportandoErrores] = useState(false);
  const [estadoCola, setEstadoCola] = useState(null);

  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [filtroVigencia, setFiltroVigencia] = useState(FILTRO_VIGENCIA.ACTIVOS);
  const reservasRef = useRef([]);
  const filtroVigenciaRef = useRef(filtroVigencia);
  const tipoOperacionRef = useRef(tipoOperacion);
  const progresoEnvioRef = useRef(null);
  filtroVigenciaRef.current = filtroVigencia;
  tipoOperacionRef.current = tipoOperacion;
  progresoEnvioRef.current = progresoEnvio;

  const showToast = useCallback((type, message) => {
    if (type === "success") toast.success(message, { position: "bottom-right", autoClose: 3000 });
    else if (type === "error") toast.error(message, { position: "bottom-right", autoClose: 4000 });
    else toast.warning(message, { position: "bottom-right", autoClose: 3000 });
  }, []);

  useEffect(() => {
    if (!token || !currentUser) navigate("/");
  }, [navigate, token, currentUser]);

  const cargarDetalle = useCallback(
    async (silencioso = false) => {
      if (!selectedProject?.id) return null;
      if (!silencioso) setLoading(true);
      try {
        const params = { tipo_operacion: tipoOperacion, limit: LIMITE_LISTADO };
        if (fechaDesde) params.fecha_desde = fechaDesde;
        if (fechaHasta) params.fecha_hasta = fechaHasta;
        if (busqueda.trim()) params.busqueda = busqueda.trim();
        if (usaFiltroVigencia(tipoOperacion)) params.vigencia = filtroVigencia;

        const [{ data }, colaResp] = await Promise.all([
          alojamientoProveedorApi.getListadoProyecto(selectedProject.id, params),
          alojamientoProveedorApi.getEstadoCola().catch(() => ({ data: { data: null } })),
        ]);

        const nuevoResumen = data.resumen || RESUMEN_INICIAL;
        const items = data.data || [];
        reservasRef.current = items;
        setReservas(items);
        setResumen(nuevoResumen);
        const colaData = colaResp.data?.data || null;
        setEstadoCola(colaData);
        return { resumen: nuevoResumen, cola: colaData };
      } catch (error) {
        if (!silencioso) {
          showToast("error", extraerMensajeError(error, "Error al cargar reservas"));
        }
        return null;
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [selectedProject?.id, fechaDesde, fechaHasta, busqueda, tipoOperacion, filtroVigencia, showToast]
  );

  useEffect(() => {
    setFiltroEstado(FILTRO_ESTADO.TODOS);
    if (usaFiltroVigencia(tipoOperacion)) {
      setFiltroVigencia(FILTRO_VIGENCIA.ACTIVOS);
    }
  }, [tipoOperacion]);

  useEffect(() => {
    if (!monitoreandoCola) return undefined;

    const intervalId = setInterval(async () => {
      const resultado = await cargarDetalle(true);
      const nuevoResumen = resultado?.resumen;
      const colaActual = resultado?.cola;
      const tipoActual = tipoOperacionRef.current;
      const visibles = reservasRef.current.filter((r) =>
        cumpleVigencia(r, tipoActual, filtroVigenciaRef.current)
      );
      const enColaVisible = contarPorEstado(visibles, tipoActual)[FILTRO_ESTADO.EN_COLA];
      const erroresVisible = contarPorEstado(visibles, tipoActual)[FILTRO_ESTADO.ERRORES];

      if (nuevoResumen && colaRedisVaciaPeroBdPendiente(enColaVisible, colaActual)) {
        pollsColaHuerfanaRef.current += 1;
        if (pollsColaHuerfanaRef.current >= 4) {
          setMonitoreandoCola(false);
          setEnviandoTodos(false);
          setProgresoEnvio(null);
          setColaHuerfanaDetectada(true);
          showToast(
            "warning",
            "Hay reservas en cola pero Redis está vacío. El worker puede estar detenido — use Reintentar o Enviar pendientes."
          );
          return;
        }
      } else {
        pollsColaHuerfanaRef.current = 0;
        setColaHuerfanaDetectada(false);
      }

      if (nuevoResumen && !hayTrabajoEnCola(enColaVisible, colaActual)) {
        setMonitoreandoCola(false);
        setEnviandoTodos(false);
        setProgresoEnvio((prev) =>
          prev ? { ...prev, procesados: prev.total } : prev
        );
        const lote = progresoEnvioRef.current;
        const erroresLote = lote?.errores ?? erroresVisible;
        const exitososLote = lote?.exitosos ?? 0;
        if (erroresLote > 0) {
          showToast(
            "error",
            `Proceso finalizado: ${exitososLote} enviada(s), ${erroresLote} error(es)`
          );
          setFiltroEstado(FILTRO_ESTADO.ERRORES);
        } else {
          showToast(
            "success",
            `Envío completado: ${exitososLote || "sin pendientes"} registro(s) al proveedor`
          );
        }
        setProgresoEnvio(null);
      } else if (nuevoResumen) {
        // Avance del lote actual (no el total histórico del proyecto)
        const enColaAhora =
          (nuevoResumen.en_cola || 0) + (nuevoResumen.procesando || 0);
        setProgresoEnvio((prev) => {
          if (!prev || !prev.total) return prev;
          const enviados = nuevoResumen.enviados_proveedor || 0;
          const errores = nuevoResumen.errores || 0;
          const baseEnv = prev.baselineEnviados ?? 0;
          const baseErr = prev.baselineErrores ?? 0;
          const exitososLote = Math.max(0, enviados - baseEnv);
          const erroresLote = Math.max(0, errores - baseErr);
          const enColaInicial = prev.enColaInicial ?? prev.total;
          const porDrenado = Math.max(0, enColaInicial - enColaAhora);
          const porResultado = exitososLote + erroresLote;
          const procesados = Math.min(
            prev.total,
            Math.max(porDrenado, porResultado)
          );
          return {
            ...prev,
            procesados,
            exitosos: exitososLote,
            errores: erroresLote,
          };
        });
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [monitoreandoCola, cargarDetalle, showToast]);

  useEffect(() => {
    if (selectedProject?.id) {
      cargarDetalle();
    }
  }, [selectedProject?.id, cargarDetalle]);

  const reservasPorVigencia = useMemo(
    () => reservas.filter((r) => cumpleVigencia(r, tipoOperacion, filtroVigencia)),
    [reservas, tipoOperacion, filtroVigencia]
  );

  const contadoresFiltro = useMemo(
    () => contarPorEstado(reservasPorVigencia, tipoOperacion),
    [reservasPorVigencia, tipoOperacion]
  );

  useEffect(() => {
    const pendientesCola =
      contadoresFiltro[FILTRO_ESTADO.EN_COLA] > 0 ||
      (estadoCola?.pendientes || 0) > 0 ||
      (estadoCola?.reintentos_programados || 0) > 0;

    if (pendientesCola && !monitoreandoCola) {
      setMonitoreandoCola(true);
    }
  }, [contadoresFiltro, estadoCola, monitoreandoCola]);

  const reservasFiltradas = useMemo(() => {
    if (filtroEstado === FILTRO_ESTADO.TODOS) return reservasPorVigencia;
    return reservasPorVigencia.filter(
      (r) => clasificarEstado(r, tipoOperacion) === filtroEstado
    );
  }, [reservasPorVigencia, filtroEstado, tipoOperacion]);

  const reservasConError = useMemo(
    () => reservas.filter((r) => clasificarEstado(r, tipoOperacion) === FILTRO_ESTADO.ERRORES),
    [reservas, tipoOperacion]
  );

  const exportarErroresExcel = async () => {
    if (!reservasConError.length) {
      showToast("warning", "No hay reservas con error para exportar");
      return;
    }

    setExportandoErrores(true);
    try {
      const detalleConLogs = await Promise.all(
        reservasConError.map(async (reserva) => {
          try {
            const { data } = await alojamientoProveedorApi.getLogsEnvio(
              reserva.asignacion_id,
              tipoOperacion
            );
            const logs = data.data?.logs || [];
            const ultimoLog = logs.length ? logs[logs.length - 1] : null;
            return { reserva, sync: data.data?.sync || null, ultimoLog, logs };
          } catch {
            return { reserva, sync: null, ultimoLog: null, logs: [] };
          }
        })
      );

      const proyectoNombre = selectedProject?.nombre || "proyecto";

      const filasDetalle = detalleConLogs.map(({ reserva, sync, ultimoLog }) => ({
        "ID asignación": reserva.asignacion_id,
        DNI: reserva.dni || "",
        Trabajador: reserva.trabajador_nombre || "",
        Zona: reserva.zona_nombre || "",
        Módulo: reserva.modulo_nombre || "",
        Habitación: reserva.habitacion_numero || "",
        Cama: reserva.cama_codigo || "",
        "ID cama Logiflex": reserva.cama_id_proveedor || "",
        "Inicio estadía": reserva.fecha_inicio_estadia || "",
        "Fin estadía": reserva.fecha_fin_estadia || "",
        "Estado asignación": reserva.estado_asignacion || "",
        Intentos: sync?.intentos ?? reserva.intentos ?? 0,
        "Estado sync": sync?.estado_envio || reserva.estado_envio || "error",
        "Fecha último envío": sync?.fecha_envio || reserva.fecha_envio || "",
        "HTTP último intento": ultimoLog?.http_status ?? "",
        "Respuesta proveedor (sync)":
          sync?.respuesta_proveedor || reserva.respuesta_proveedor || "",
        "URL solicitud (último intento)": ultimoLog?.request_url || "",
        "Respuesta completa (último intento)": ultimoLog?.response_body || "",
      }));

      const filasResumen = [
        { Campo: "Proyecto", Valor: proyectoNombre },
        { Campo: "Total reservas (filtro)", Valor: resumen.total },
        { Campo: "Total reservas listadas", Valor: resumen.listadas ?? reservas.length },
        { Campo: "Total errores exportados", Valor: reservasConError.length },
        {
          Campo: "Generado el",
          Valor: new Date().toLocaleString("es-PE", { timeZone: "America/Lima" }),
        },
        {
          Campo: "Nota",
          Valor:
            "Detalle de reservas no sincronizadas con Logiflex/Cumbrak para validación del proveedor.",
        },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasResumen), "Resumen");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasDetalle), "Errores");

      const slugProyecto = proyectoNombre.replace(/[^\w]+/g, "_").slice(0, 30);
      XLSX.writeFile(
        wb,
        `Errores_Logiflex_Reservas_${slugProyecto}_${Date.now()}.xlsx`
      );

      showToast(
        "success",
        `Excel exportado: ${reservasConError.length} error(es) para enviar al proveedor`
      );
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al exportar errores a Excel"));
    } finally {
      setExportandoErrores(false);
    }
  };

  const abrirDetalleError = async (record) => {
    setModalErrorOpen(true);
    setErrorDetalle({ record, sync: null, logs: [] });
    setLoadingErrorDetalle(true);
    try {
      const { data } = await alojamientoProveedorApi.getLogsEnvio(
        record.asignacion_id,
        tipoOperacion
      );
      setErrorDetalle({
        record,
        sync: data.data?.sync || null,
        logs: data.data?.logs || [],
      });
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al cargar detalle del envío"));
      setErrorDetalle({
        record,
        sync: record.respuesta_proveedor
          ? { respuesta_proveedor: record.respuesta_proveedor, intentos: record.intentos }
          : null,
        logs: [],
      });
    } finally {
      setLoadingErrorDetalle(false);
    }
  };

  const enviarReserva = async (record, forzarReenvio = false) => {
    if (record.puede_sincronizar === false && !tieneCadenaAuto(record)) {
      showToast("warning", record.bloqueo_sincronizacion || "No se puede sincronizar esta operación");
      return;
    }

    const debeForzar =
      forzarReenvio ||
      record.estado_envio === "error" ||
      record.estado_envio === "reintento" ||
      record.estado_envio === "en_cola" ||
      record.estado_envio === "procesando";

    setEnviandoId(record.asignacion_id);
    iniciarProgresoLote(1);
    try {
      const { data } = await alojamientoProveedorApi.encolarReserva(record.asignacion_id, {
        forzarReenvio: debeForzar,
        origen: "popup",
        tipoOperacion,
      });
      const result = data.data || {};
      if (result.omitido) {
        setProgresoEnvio(null);
        const motivo = data.message || result.motivo || "No se encoló la operación";
        const yaExitoso = /exitosamente|ya fue enviada|ya estaba sincronizado/i.test(
          String(motivo)
        );
        showToast(yaExitoso ? "info" : "warning", motivo);
        if (yaExitoso) {
          await cargarDetalle(true);
        }
      } else if (result.ya_en_cola && !debeForzar) {
        setMonitoreandoCola(true);
        showToast("info", data.message || "La operación ya estaba en cola");
      } else {
        const msg = data.message || "Operación encolada para envío al proveedor";
        showToast(
          "success",
          result.cadena_pendiente && !/cadena/i.test(msg)
            ? `${msg} El worker enviará primero el check-in de la misma asignación.`
            : msg
        );
        setMonitoreandoCola(true);
      }
      await cargarDetalle(true);
    } catch (error) {
      setProgresoEnvio(null);
      showToast("error", extraerMensajeError(error, "Error al encolar reserva al proveedor"));
    } finally {
      setEnviandoId(null);
    }
  };

  const iniciarProgresoLote = (totalLote) => {
    const total = Math.max(0, Number(totalLote) || 0);
    setProgresoEnvio({
      total,
      procesados: 0,
      exitosos: 0,
      errores: 0,
      baselineEnviados: resumen.enviados_proveedor || 0,
      baselineErrores: resumen.errores || 0,
      enColaInicial: total,
    });
  };

  const enviarPendientes = async () => {
    const hayParaEnviar =
      contadoresFiltro[FILTRO_ESTADO.PENDIENTES] +
        contadoresFiltro[FILTRO_ESTADO.EN_COLA] +
        contadoresFiltro[FILTRO_ESTADO.ERRORES] >
      0;
    if (!hayParaEnviar) {
      showToast("warning", "No hay registros pendientes para enviar");
      return;
    }

    const totalEstimado =
      contadoresFiltro[FILTRO_ESTADO.PENDIENTES] +
      contadoresFiltro[FILTRO_ESTADO.EN_COLA] +
      contadoresFiltro[FILTRO_ESTADO.ERRORES];

    // Si BD tiene cola pero Redis no, forzar reencolado (cola huérfana / worker caído).
    const forzarReenvio =
      colaHuerfanaDetectada ||
      (contadoresFiltro[FILTRO_ESTADO.EN_COLA] > 0 && !(estadoCola?.pendientes > 0));

    setEnviandoTodos(true);
    setColaHuerfanaDetectada(false);
    pollsColaHuerfanaRef.current = 0;
    iniciarProgresoLote(totalEstimado);

    try {
      const { data } = await alojamientoProveedorApi.encolarPendientesProyecto(
        selectedProject.id,
        {
          tipoOperacion,
          forzarReenvio,
          vigencia: usaFiltroVigencia(tipoOperacion) ? filtroVigencia : undefined,
        }
      );
      const encolados = Number(data.data?.encolados || 0);
      if (encolados > 0) {
        setProgresoEnvio((prev) =>
          prev
            ? {
                ...prev,
                total: encolados,
                enColaInicial: encolados,
                procesados: 0,
              }
            : prev
        );
        setMonitoreandoCola(true);
        showToast(
          "success",
          data.message ||
            `${encolados} reserva(s) encolada(s). El worker las enviará al proveedor.`
        );
      } else {
        // Nada nuevo en Redis: no dejar el botón en "Procesando cola…" vacío
        setProgresoEnvio(null);
        const hayColaBd =
          contadoresFiltro[FILTRO_ESTADO.EN_COLA] > 0 ||
          (estadoCola?.pendientes || 0) > 0;
        if (hayColaBd) {
          setMonitoreandoCola(true);
          showToast(
            "warning",
            data.message ||
              "No se encolaron registros nuevos. Si la cola no avanza, use Reintentar o revise el worker."
          );
        } else {
          setMonitoreandoCola(false);
          showToast(
            "info",
            data.message || "No había registros nuevos para encolar en esta operación."
          );
        }
      }
      await cargarDetalle(true);
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al encolar reservas al proveedor"));
      setProgresoEnvio(null);
      setMonitoreandoCola(false);
    } finally {
      // Solo bloquea el botón durante el POST; el progreso vive en la barra / monitor
      setEnviandoTodos(false);
    }
  };

  const porcentajeProgreso =
    progresoEnvio && progresoEnvio.total > 0
      ? Math.min(
          100,
          Math.round((progresoEnvio.procesados / progresoEnvio.total) * 100)
        )
      : 0;

  const renderEstadoProveedor = (_, record) => {
    if (normalizarEnviadoProveedor(record.enviado_proveedor) || record.estado_envio === "exitoso") {
      return (
        <Tooltip title={record.fecha_envio ? `Enviado: ${record.fecha_envio}` : ""}>
          <Tag color="success">Enviado</Tag>
        </Tooltip>
      );
    }
    if (record.estado_envio === "error") {
      return (
        <Tooltip title={truncarTexto(record.respuesta_proveedor, 200)}>
          <Tag color="error">Error</Tag>
        </Tooltip>
      );
    }
    if (record.estado_envio === "procesando") {
      return <Tag color="processing">Procesando</Tag>;
    }
    if (record.estado_envio === "reintento") {
      return (
        <Tooltip title={truncarTexto(record.respuesta_proveedor, 200) || "Esperando reintento automático"}>
          <Tag color="orange">Reintento {record.intentos || 0}</Tag>
        </Tooltip>
      );
    }
    if (record.estado_envio === "en_cola") {
      return <Tag color="blue">En cola</Tag>;
    }
    if (tieneCadenaAuto(record) && !esEnvioExitoso(record)) {
      const pasos = record.cadena_auto.join(" → ");
      return (
        <Tooltip title={`Al enviar se sincronizará primero: ${pasos} → ${tipoOperacion}`}>
          <Tag color="cyan">Cadena: {pasos}</Tag>
        </Tooltip>
      );
    }
    if (record.puede_sincronizar === false && !esEnvioExitoso(record)) {
      const detallePrereq =
        tipoOperacion === TIPO_OPERACION.CHECKOUT
          ? `Check-in previo: ${record.checkin_sincronizado ? "OK" : "pendiente"}`
          : "";
      const titulo = [record.bloqueo_sincronizacion, detallePrereq].filter(Boolean).join(" · ");
      return (
        <Tooltip title={titulo || "Operación previa pendiente en Logiflex"}>
          <Tag color="warning">Bloqueado</Tag>
        </Tooltip>
      );
    }
    return <Tag color="default">Pendiente</Tag>;
  };

  const renderPrerequisito = (ok, etiqueta) =>
    ok ? (
      <Tag color="success">{etiqueta} OK</Tag>
    ) : (
      <Tag color="warning">{etiqueta} pendiente</Tag>
    );

  const columnasPrerequisito = useMemo(() => {
    if (tipoOperacion === TIPO_OPERACION.CHECKOUT) {
      return [
        {
          title: "Check-in previo",
          key: "prereq_checkin",
          width: 125,
          render: (_, r) => renderPrerequisito(r.checkin_sincronizado, "Check-in"),
        },
      ];
    }
    return [];
  }, [tipoOperacion]);

  const columnasFechaOperacion = useMemo(() => {
    if (tipoOperacion === TIPO_OPERACION.CHECKIN) {
      return [
        {
          title: "Check-in SGC",
          dataIndex: "fecha_check_in",
          key: "fecha_check_in",
          width: 150,
          render: (v) => v || "—",
        },
      ];
    }
    if (tipoOperacion === TIPO_OPERACION.CHECKOUT) {
      return [
        {
          title: "Check-out SGC",
          dataIndex: "fecha_check_out",
          key: "fecha_check_out",
          width: 150,
          defaultSortOrder: "descend",
          sorter: (a, b) => compararFechaListado(a.fecha_check_out, b.fecha_check_out),
          render: (v) => v || "—",
        },
      ];
    }
    return [
      {
        title: "Estadía",
        key: "estadia",
        width: 170,
        render: (_, r) =>
          r.fecha_inicio_estadia && r.fecha_fin_estadia
            ? `${r.fecha_inicio_estadia} → ${r.fecha_fin_estadia}`
            : "—",
      },
    ];
  }, [tipoOperacion]);

  const columns = useMemo(
    () => [
    {
      title: "ID",
      dataIndex: "asignacion_id",
      key: "asignacion_id",
      width: 70,
    },
    {
      title: "DNI",
      dataIndex: "dni",
      key: "dni",
      width: 100,
      render: (v) => v || "—",
    },
    {
      title: "Trabajador",
      dataIndex: "trabajador_nombre",
      key: "trabajador_nombre",
      ellipsis: true,
    },
    {
      title: "Ubicación",
      key: "ubicacion",
      width: 180,
      ellipsis: true,
      render: (_, r) =>
        [r.zona_nombre, r.modulo_nombre, r.habitacion_numero, r.cama_codigo]
          .filter(Boolean)
          .join(" / ") || "—",
    },
    ...columnasFechaOperacion,
    ...columnasPrerequisito,
    {
      title: "Estado SGC",
      dataIndex: "estado_asignacion",
      key: "estado_asignacion",
      width: 110,
      render: (estado) => {
        const nombre = String(estado || "—");
        const key = nombre.toLowerCase();
        const color =
          key === "ocupada" ? "green" : key === "reservada" ? "blue" : key === "finalizada" ? "purple" : "default";
        return <Tag color={color}>{nombre}</Tag>;
      },
    },
    {
      title: "Estado proveedor",
      key: "enviado_proveedor",
      width: 140,
      render: renderEstadoProveedor,
    },
    {
      title: "Intentos",
      dataIndex: "intentos",
      key: "intentos",
      width: 75,
      align: "center",
      render: (v) => v || 0,
    },
    {
      title: "Acción",
      key: "accion",
      width: 210,
      fixed: "right",
      render: (_, record) => {
        const bloqueadoSinCadena =
          record.puede_sincronizar === false && !tieneCadenaAuto(record);

        if (esEnvioExitoso(record)) {
          return <span className="text-muted small">Sincronizado</span>;
        }
        if (bloqueadoSinCadena) {
          return (
            <Tooltip title={record.bloqueo_sincronizacion}>
              <span className="text-muted small">No disponible</span>
            </Tooltip>
          );
        }

        const etiquetaAccion =
          record.estado_envio === "error" ||
          record.estado_envio === "reintento" ||
          estaEnCola(record)
            ? "Reintentar"
            : tieneCadenaAuto(record)
              ? "Enviar cadena"
              : "Enviar";

        return (
          <Space size="small" wrap className="sync-reservas-acciones">
            {estaEnCola(record) && <span className="text-muted small">En cola…</span>}
            {puedeVerDetalleError(record) && (
              <Button
                size="sm"
                className="btn-dh-secondary"
                disabled={enviandoTodos}
                onClick={() => abrirDetalleError(record)}
              >
                <MdErrorOutline size={16} />
                Ver error
              </Button>
            )}
            <Tooltip
              title={
                tieneCadenaAuto(record)
                  ? `Se enviará: ${(record.cadena_auto || []).join(" → ")} → ${tipoOperacion}`
                  : undefined
              }
            >
              <Button
                size="sm"
                className="btn-dh-primary"
                disabled={enviandoTodos || enviandoId === record.asignacion_id}
                onClick={() => enviarReserva(record, true)}
              >
                <MdCloudUpload size={16} />
                {enviandoId === record.asignacion_id ? "…" : etiquetaAccion}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ],
    [columnasFechaOperacion, columnasPrerequisito, enviandoId, enviandoTodos, tipoOperacion]
  );

  const opcionesFiltroEstado = useMemo(() => {
    const base = [
      {
        label: `Todos (${contadoresFiltro[FILTRO_ESTADO.TODOS]})`,
        value: FILTRO_ESTADO.TODOS,
      },
      {
        label: `Pendientes (${contadoresFiltro[FILTRO_ESTADO.PENDIENTES]})`,
        value: FILTRO_ESTADO.PENDIENTES,
      },
      {
        label: `En cola (${contadoresFiltro[FILTRO_ESTADO.EN_COLA]})`,
        value: FILTRO_ESTADO.EN_COLA,
      },
      {
        label: `Enviadas (${contadoresFiltro[FILTRO_ESTADO.ENVIADOS]})`,
        value: FILTRO_ESTADO.ENVIADOS,
      },
      {
        label: `Errores (${contadoresFiltro[FILTRO_ESTADO.ERRORES]})`,
        value: FILTRO_ESTADO.ERRORES,
      },
    ];
    if (contadoresFiltro[FILTRO_ESTADO.SIN_HOMOLOGAR] > 0) {
      base.push({
        label: `Sin homologar (${contadoresFiltro[FILTRO_ESTADO.SIN_HOMOLOGAR]})`,
        value: FILTRO_ESTADO.SIN_HOMOLOGAR,
      });
    }
    if (contadoresFiltro[FILTRO_ESTADO.BLOQUEADOS] > 0) {
      base.push({
        label: `Bloqueados (${contadoresFiltro[FILTRO_ESTADO.BLOQUEADOS]})`,
        value: FILTRO_ESTADO.BLOQUEADOS,
      });
    }
    return base;
  }, [contadoresFiltro]);

  const logColumns = [
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 90,
      render: (estado) => (
        <Tag color={estado === "exitoso" ? "success" : "error"}>{estado}</Tag>
      ),
    },
    {
      title: "HTTP",
      dataIndex: "http_status",
      key: "http_status",
      width: 70,
      render: (v) => v ?? "—",
    },
    {
      title: "Respuesta proveedor",
      dataIndex: "response_body",
      key: "response_body",
      ellipsis: true,
      render: (v) => truncarTexto(v, 200),
    },
  ];

  if (!selectedProject?.id) {
    return (
      <div className="dh-reporte-bg min-vh-100 d-flex align-items-center justify-content-center">
        <style>
          {`
            .dh-reporte-bg {
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              min-height: 100vh;
            }
            .dh-alert-info {
              background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
              border: 1px solid #93c5fd;
              border-radius: 12px;
              padding: 1rem 1.25rem;
              color: #1e40af;
            }
          `}
        </style>
        <div className="dh-alert dh-alert-info mx-3">
          Seleccione un proyecto de campamento para continuar
        </div>
      </div>
    );
  }

  return (
    <div className="dh-reporte-bg">
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding-bottom: 2rem;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 1.5rem;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .btn-dh-primary:hover:not(:disabled) { color: white; opacity: 0.95; }
          .btn-dh-primary:disabled { opacity: 0.65; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .btn-dh-secondary:hover:not(:disabled) { color: white; opacity: 0.95; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control {
            border-radius: 10px;
            border-color: #e2e8f0;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.2);
          }
          .dh-alert-info {
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            border: 1px solid #93c5fd;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            color: #1e40af;
          }
          .sync-reservas .resumen-bar {
            background: linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%);
            border-radius: 10px;
            padding: 12px 16px;
            margin-bottom: 16px;
            border: 1px solid #e2e8f0;
          }
          .sync-reservas .resumen-bar strong { color: #667eea; }
          .sync-reservas .progreso-envio-panel {
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 14px 16px;
            margin-bottom: 16px;
          }
          .sync-reservas .progreso-envio-stats {
            display: flex;
            flex-wrap: wrap;
            gap: 12px 20px;
            margin-top: 10px;
            font-size: 0.875rem;
          }
          .sync-reservas .progreso-envio-stats .stat-exito { color: #059669; font-weight: 600; }
          .sync-reservas .progreso-envio-stats .stat-error { color: #dc2626; font-weight: 600; }
          .sync-reservas .progreso-envio-stats .stat-pendiente { color: #667eea; font-weight: 600; }
          .sync-reservas .filtro-estado-bar { margin-bottom: 12px; }
          .sync-reservas .log-url-box {
            background: #f8f9fc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 8px 10px;
            font-size: 0.75rem;
            word-break: break-all;
            max-height: 80px;
            overflow-y: auto;
          }
          .sync-reservas .log-response-box {
            background: #fff5f5;
            border: 1px solid #fecaca;
            border-radius: 8px;
            padding: 10px 12px;
            font-size: 0.8rem;
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 160px;
            overflow-y: auto;
          }
          .sync-reservas .ant-table-thead > tr > th {
            background: #f1f5f9 !important;
            color: #374151;
            font-weight: 600;
          }
          .sync-reservas .ant-table-tbody > tr:hover > td {
            background: #f8fafc !important;
          }
          .sync-reservas .ant-segmented {
            background: #f1f5f9;
            padding: 4px;
            border-radius: 10px;
          }
          .sync-reservas .ant-segmented-item-selected {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
            color: white !important;
            border-radius: 8px !important;
          }
          .sync-reservas-acciones .btn {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 0.8rem;
            font-weight: 600;
            border-radius: 8px;
            padding: 4px 10px;
            white-space: nowrap;
          }
          .dh-modal .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 15px 35px rgba(0,0,0,0.15);
          }
          .dh-modal .modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.25rem 1.5rem;
          }
          .dh-modal .modal-title { font-weight: 700; }
          .dh-modal .btn-close { filter: invert(1); opacity: 0.9; }
          .dh-modal .modal-body { padding: 1.5rem; }
          .dh-modal .modal-footer {
            border-top: 1px solid #e2e8f0;
            padding: 1rem 1.5rem;
          }
        `}
      </style>

      <ToastContainer />

      <div className="dh-main-header">
        <Container fluid="lg">
          <h1 className="dh-main-title d-flex align-items-center gap-2">
            <MdHotel size={32} />
            Sincronización Alojamiento Logiflex
          </h1>
          <p className="dh-main-subtitle">
            Proyecto: {selectedProject.nombre} — {SUBTITULO_TIPO[tipoOperacion]}
          </p>
        </Container>
      </div>

      <Container fluid="lg">
        <Card className="dh-card">
          <Card.Header className="dh-card-header dh-card-header-primary">
            Tipo de operación
          </Card.Header>
          <Card.Body className="py-3 sync-reservas">
            <Segmented
              value={tipoOperacion}
              onChange={(v) => setTipoOperacion(v)}
              disabled={enviandoTodos}
              options={[
                { label: ETIQUETAS_TIPO[TIPO_OPERACION.CHECKIN], value: TIPO_OPERACION.CHECKIN },
                { label: ETIQUETAS_TIPO[TIPO_OPERACION.CHECKOUT], value: TIPO_OPERACION.CHECKOUT },
              ]}
            />
            {tipoOperacion === TIPO_OPERACION.CHECKOUT && (
              <Alert
                className="mt-3 mb-0"
                type="info"
                showIcon
                message="Cadena automática check-in → check-out"
                description="Al enviar un check-out, si falta el check-in Logiflex de la misma asignación, el sistema lo sincroniza primero. No exige reserva previa ni usa check-ins de otras estadías del mismo DNI."
              />
            )}
            {tipoOperacion === TIPO_OPERACION.CHECKIN && (
              <Alert
                className="mt-3 mb-0"
                type="info"
                showIcon
                message="Check-in independiente de reserva"
                description="El check-in se envía a Logiflex sin exigir que exista una reserva sincronizada ni que las fechas de reserva cubran el check-in."
              />
            )}
          </Card.Body>
        </Card>

        <Card className="dh-card">
          <Card.Header className="dh-card-header dh-card-header-primary">
            Filtros de búsqueda
          </Card.Header>
          <Card.Body>
            {usaFiltroVigencia(tipoOperacion) && (
              <div className="mb-3">
                <Form.Label className="dh-form-label d-block mb-2">
                  Vigencia del check-in
                </Form.Label>
                <Segmented
                  value={filtroVigencia}
                  onChange={(v) => {
                    setFiltroVigencia(v);
                    setFiltroEstado(FILTRO_ESTADO.TODOS);
                  }}
                  disabled={enviandoTodos}
                  options={[
                    { label: "Activos", value: FILTRO_VIGENCIA.ACTIVOS },
                    { label: "Inactivos", value: FILTRO_VIGENCIA.INACTIVOS },
                    { label: "Todos", value: FILTRO_VIGENCIA.TODOS },
                  ]}
                />
                <div className="small text-muted mt-2">
                  Activos: aún no tienen check-out. Inactivos: ya tienen check-out. Cambiar vigencia vuelve a consultar el servidor.
                </div>
              </div>
            )}
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Label className="dh-form-label">Desde</Form.Label>
                <Form.Control
                  type="date"
                  className="dh-form-control"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="dh-form-label">Hasta</Form.Label>
                <Form.Control
                  type="date"
                  className="dh-form-control"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </Col>
              <Col md={4}>
                <Form.Label className="dh-form-label">Buscar (DNI, nombre, cama, ID)</Form.Label>
                <Form.Control
                  type="text"
                  className="dh-form-control"
                  placeholder="Ej. 12345678 o Pérez"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Button
                  className="btn-dh-primary w-100"
                  onClick={() => cargarDetalle()}
                  disabled={loading || enviandoTodos}
                >
                  Buscar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="dh-card">
          <Card.Header className="dh-card-header dh-card-header-success">
            Resultados — {ETIQUETAS_TIPO[tipoOperacion]}
            {usaFiltroVigencia(tipoOperacion)
              ? ` · ${
                  filtroVigencia === FILTRO_VIGENCIA.ACTIVOS
                    ? "Activos"
                    : filtroVigencia === FILTRO_VIGENCIA.INACTIVOS
                      ? "Inactivos"
                      : "Todos"
                }`
              : ""}
          </Card.Header>
          <Card.Body>
            <div className="sync-reservas">
              <div className="resumen-bar d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <div className="small text-muted mb-1">
                    Cola Redis:{" "}
                    {estadoCola?.redis_ok
                      ? `${estadoCola.pendientes || 0} pendiente(s), ${estadoCola.reintentos_programados || 0} reintento(s)`
                      : "—"}
                  </div>
                  <div>
                    Total:{" "}
                    <strong>{contadoresFiltro[FILTRO_ESTADO.TODOS]}</strong>
                    {filtroVigencia === FILTRO_VIGENCIA.TODOS && resumen.truncado ? (
                      <span className="text-muted">
                        {" "}
                        (mostrando {resumen.listadas || reservas.length} de {resumen.total})
                      </span>
                    ) : null}{" "}
                    · Enviadas:{" "}
                    <strong>{contadoresFiltro[FILTRO_ESTADO.ENVIADOS]}</strong> · Pendientes:{" "}
                    <strong>{contadoresFiltro[FILTRO_ESTADO.PENDIENTES]}</strong> · En cola:{" "}
                    <strong>{contadoresFiltro[FILTRO_ESTADO.EN_COLA]}</strong> · Errores:{" "}
                    <strong
                      style={{
                        color:
                          contadoresFiltro[FILTRO_ESTADO.ERRORES] > 0 ? "#dc2626" : undefined,
                      }}
                    >
                      {contadoresFiltro[FILTRO_ESTADO.ERRORES]}
                    </strong>
                    {contadoresFiltro[FILTRO_ESTADO.SIN_HOMOLOGAR] > 0 && (
                      <>
                        {" "}
                        · Sin homologar:{" "}
                        <strong style={{ color: "#d97706" }}>
                          {contadoresFiltro[FILTRO_ESTADO.SIN_HOMOLOGAR]}
                        </strong>
                      </>
                    )}
                    {contadoresFiltro[FILTRO_ESTADO.BLOQUEADOS] > 0 && (
                      <>
                        {" "}
                        · Bloqueados:{" "}
                        <strong style={{ color: "#d97706" }}>
                          {contadoresFiltro[FILTRO_ESTADO.BLOQUEADOS]}
                        </strong>
                      </>
                    )}
                  </div>
                </div>
                <Space wrap>
                  <Button
                    onClick={exportarErroresExcel}
                    disabled={!reservasConError.length || enviandoTodos || exportandoErrores}
                    className="btn-dh-secondary"
                  >
                    <MdDownload size={18} />
                    {exportandoErrores ? "Exportando…" : "Exportar errores Excel"}
                  </Button>
                  <Button
                    onClick={() => cargarDetalle()}
                    disabled={enviandoTodos}
                    className="btn-dh-secondary"
                  >
                    <MdRefresh size={18} />
                    Actualizar
                  </Button>
                  <Button
                    className="btn-dh-primary"
                    disabled={
                      contadoresFiltro[FILTRO_ESTADO.PENDIENTES] +
                        contadoresFiltro[FILTRO_ESTADO.EN_COLA] +
                        contadoresFiltro[FILTRO_ESTADO.ERRORES] ===
                        0 || enviandoTodos
                    }
                    onClick={enviarPendientes}
                  >
                    <MdCloudUpload size={18} />
                    {enviandoTodos
                      ? "Encolando…"
                      : progresoEnvio
                        ? `En cola ${progresoEnvio.procesados}/${progresoEnvio.total}`
                        : "Enviar pendientes"}
                  </Button>
                </Space>
              </div>

              <div className="filtro-estado-bar">
                <Segmented
                  value={filtroEstado}
                  onChange={(v) => {
                    setFiltroEstado(v);
                  }}
                  disabled={enviandoTodos}
                  options={opcionesFiltroEstado}
                />
              </div>

              {resumen.truncado && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message="Listado incompleto"
                  description={`Se muestran ${resumen.listadas || reservas.length} de ${resumen.total} registros (límite ${resumen.limite || LIMITE_LISTADO}). Acote fechas o búsqueda para ver el resto. Los contadores de estado corresponden solo a lo listado.`}
                />
              )}

              {colaHuerfanaDetectada && (
                <Alert
                  type="warning"
                  showIcon
                  className="dh-alert dh-alert-info mb-3"
                  message="Cola detenida"
                  description="Hay reservas marcadas en cola pero Redis no tiene jobs pendientes. Verifique que el worker esté activo o use Reintentar en cada fila / Enviar pendientes."
                />
              )}

              {progresoEnvio && (
                <div className="progreso-envio-panel">
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <strong style={{ color: "#374151" }}>
                      Envío al proveedor en segundo plano (cola Redis)…
                    </strong>
                    <span className="text-muted small">
                      {`${progresoEnvio.procesados} de ${progresoEnvio.total} (${porcentajeProgreso}%)`}
                    </span>
                  </div>
                  <Progress
                    percent={porcentajeProgreso}
                    status={progresoEnvio.errores > 0 ? "exception" : "active"}
                    strokeColor={{ from: "#667eea", to: "#764ba2" }}
                  />
                  <div className="progreso-envio-stats">
                    <span className="stat-exito">Exitosas: {progresoEnvio.exitosos}</span>
                    <span className="stat-error">Errores: {progresoEnvio.errores}</span>
                    <span className="stat-pendiente">
                      En cola: {contadoresFiltro[FILTRO_ESTADO.EN_COLA]}
                    </span>
                  </div>
                </div>
              )}
              {!progresoEnvio && monitoreandoCola && (
                <div className="small text-muted mb-2">
                  Actualizando estado de cola en segundo plano…
                </div>
              )}

              <div className="dh-alert dh-alert-info mb-3 small">
                Los check-in y check-out se encolan y el worker los envía a Logiflex/Cumbrak.
                Estado <strong>Reintento N</strong>: falló un intento y se reintentará
                automáticamente (30s, 2min, 5min). Use <strong>Ver error</strong> para ver la
                respuesta del proveedor.
              </div>

              <Table
                rowKey="asignacion_id"
                columns={columns}
                dataSource={reservasFiltradas}
                loading={loading && !enviandoTodos}
                size="small"
                scroll={{ x: 1100 }}
                pagination={{ pageSize: 15, showSizeChanger: true }}
                locale={{
                  emptyText:
                    filtroEstado === FILTRO_ESTADO.TODOS
                      ? "No hay registros de esta operación en este proyecto"
                      : "No hay registros con el filtro seleccionado",
                }}
              />
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Modal
        title={
          <span className="d-flex align-items-center gap-2">
            <MdErrorOutline color="#dc2626" />
            Detalle de error — {errorDetalle?.record?.trabajador_nombre || "Asignación"}
          </span>
        }
        open={modalErrorOpen}
        onCancel={() => setModalErrorOpen(false)}
        footer={
          <Button className="btn-dh-secondary" onClick={() => setModalErrorOpen(false)}>
            Cerrar
          </Button>
        }
        width={820}
        destroyOnClose
        className="dh-modal"
      >
        {loadingErrorDetalle ? (
          <div className="text-center py-4">
            <Spin tip="Cargando historial de envío…" />
          </div>
        ) : (
          <>
            <Descriptions bordered size="small" column={2} className="mb-3">
              <Descriptions.Item label="DNI">{errorDetalle?.record?.dni || "—"}</Descriptions.Item>
              <Descriptions.Item label="ID asignación">
                {errorDetalle?.record?.asignacion_id || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Intentos">
                {errorDetalle?.sync?.intentos ?? errorDetalle?.record?.intentos ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Estado sync">
                <Tag color="error">{errorDetalle?.sync?.estado_envio || "error"}</Tag>
              </Descriptions.Item>
            </Descriptions>

            {(errorDetalle?.sync?.respuesta_proveedor ||
              errorDetalle?.record?.respuesta_proveedor) && (
              <div className="mb-3">
                <div className="small fw-semibold mb-1">Última respuesta del proveedor</div>
                <div className="log-response-box">
                  {errorDetalle?.sync?.respuesta_proveedor ||
                    errorDetalle?.record?.respuesta_proveedor}
                </div>
              </div>
            )}

            <div className="small fw-semibold mb-2">
              Historial de intentos ({errorDetalle?.logs?.length || 0})
            </div>
            <Table
              rowKey="id"
              size="small"
              columns={logColumns}
              dataSource={errorDetalle?.logs || []}
              pagination={false}
              expandable={{
                expandedRowRender: (log) => (
                  <div className="py-1">
                    {log.request_url && (
                      <div className="mb-2">
                        <div className="small text-muted mb-1">URL de solicitud</div>
                        <div className="log-url-box">{log.request_url}</div>
                      </div>
                    )}
                    {log.response_body && (
                      <div>
                        <div className="small text-muted mb-1">Cuerpo de respuesta</div>
                        <div className="log-response-box">{log.response_body}</div>
                      </div>
                    )}
                  </div>
                ),
                rowExpandable: (log) => Boolean(log.request_url || log.response_body),
              }}
              locale={{ emptyText: "Sin registros en alojamientos_proveedor_envio_log" }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default SincronizacionReservasLogiflex;
